import styled, { createGlobalStyle } from 'styled-components';
import { Select } from 'antd';

// Standard color palette for consistency
const colors = {
  primary: '#4A90E2',      // Calming blue
  secondary: '#7FB3D5',    // Soft blue
  text: '#2C3E50',         // Dark blue-gray
  error: '#d9534f',        // Soft red
  border: '#d9d9d9',       // Standard border color
};

// Global styles for select dropdowns (admin-style)
export const GlobalSelectStyles = createGlobalStyle`
  .ant-select-dropdown {
    border-radius: 8px !important;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12) !important;
    border: 1px solid ${colors.border} !important;
    padding: 6px 0 !important;
    z-index: 1050 !important;
    background: white !important;
    overflow: hidden !important;
    
    /* Mobile-specific fixes */
    @media (max-width: 768px) {
      position: fixed !important;
      max-height: 70vh !important;
      overflow-y: auto !important;
      -webkit-overflow-scrolling: touch !important;
    }
  }

  .ant-select-item {
    padding: 10px 16px !important;
    min-height: 40px !important;
    display: flex !important;
    align-items: center !important;
    font-size: 14px !important;
    line-height: 1.4 !important;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1) !important;
    color: #333 !important;
    margin: 0 4px !important;
    border-radius: 4px !important;
    cursor: pointer !important;
    
    /* Larger touch targets on mobile */
    @media (max-width: 768px) {
      min-height: 44px !important;
      padding: 12px 16px !important;
      font-size: 16px !important;
    }
    
    &:hover {
      background-color: ${colors.primary} !important;
      color: white !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 2px 4px rgba(74, 144, 226, 0.2) !important;
    }
    
    &.ant-select-item-option-selected {
      background-color: ${colors.primary} !important;
      color: white !important;
      font-weight: 500 !important;
      
      &::after {
        content: "✓" !important;
        margin-left: auto !important;
        font-weight: bold !important;
        color: white !important;
      }
    }
    
    &.ant-select-item-option-active {
      background-color: rgba(74, 144, 226, 0.1) !important;
      color: ${colors.primary} !important;
      border: 1px solid ${colors.primary} !important;
    }

    &.ant-select-item-option-selected.ant-select-item-option-active {
      background-color: ${colors.primary} !important;
      color: white !important;
      border: 1px solid transparent !important;
    }
    
    &.ant-select-item-option-disabled {
      color: #999 !important;
      cursor: not-allowed !important;
      background: none !important;
      
      &:hover {
        background: none !important;
        transform: none !important;
        box-shadow: none !important;
      }
    }
  }

  .ant-select-item-option-content {
    flex: 1;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
  }

  .ant-select-item-empty {
    color: #999 !important;
    text-align: center !important;
    font-style: italic !important;
    padding: 16px !important;
  }
`;

// Styled Select component (matching admin panel styling)
export const StyledSelect = styled(Select)`
  height: 48px !important;
  
  .ant-select-selector {
    height: 48px !important;
    border-radius: 8px !important;
    border: 1px solid ${colors.border} !important;
    box-shadow: none !important;
    font-size: 16px !important;
    padding: 0 11px !important;
    display: flex !important;
    align-items: center !important;
    line-height: 1 !important;
    
    &:hover {
      border-color: ${colors.primary} !important;
      box-shadow: none !important;
    }
  }
  
  &.ant-select-focused .ant-select-selector,
  &.ant-select-open .ant-select-selector {
    border-color: ${colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2) !important;
  }
  
  .ant-select-selection-item {
    line-height: 1.5 !important;
    height: auto !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    color: ${colors.text} !important;
  }
  
  .ant-select-selection-placeholder {
    line-height: 1.5 !important;
    height: auto !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    color: #bfbfbf !important;
  }
  
  .ant-select-arrow {
    top: 50% !important;
    transform: translateY(-50%) !important;
    right: 11px !important;
    margin-top: 0 !important;
    height: 12px !important;
    width: 12px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    
    .anticon {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-size: 12px !important;
      line-height: 1 !important;
    }
  }

  .ant-select-clear {
    top: 50% !important;
    transform: translateY(-50%) !important;
    right: 35px !important;
    margin-top: 0 !important;
  }
  
  /* Error state styling */
  &.ant-select-status-error .ant-select-selector {
    border-color: ${colors.error} !important;
    box-shadow: 0 0 0 2px rgba(217, 83, 79, 0.2) !important;
  }
`;

// Large Select component for form wizards
export const LargeSelect = styled(StyledSelect)`
  height: 48px !important;
  font-size: 16px !important;
`;

export default StyledSelect;
